import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

interface ScrollRevealProps {
  children: React.ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  delay?: number;
  duration?: number;
  once?: boolean;
  amount?: number;
  className?: string;
}

/**
 * ScrollReveal - Reusable component for scroll-triggered animations
 *
 * Usage:
 * <ScrollReveal direction="up" delay={0.2}>
 *   <h2>Your Content</h2>
 * </ScrollReveal>
 */
const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  direction = 'up',
  delay = 0,
  duration = 0.8,
  once = true,
  amount = 0.3,
  className = '',
}) => {
  const shouldReduceMotion = useReducedMotion();

  // Initial position based on direction
  const getInitialPosition = () => {
    if (shouldReduceMotion) return { opacity: 0 };

    switch (direction) {
      case 'up':
        return { opacity: 0, y: 50 };
      case 'down':
        return { opacity: 0, y: -50 };
      case 'left':
        return { opacity: 0, x: 50 };
      case 'right':
        return { opacity: 0, x: -50 };
      case 'none':
      default:
        return { opacity: 0 };
    }
  };

  // Animate to position
  const getAnimatePosition = () => {
    if (shouldReduceMotion) return { opacity: 1 };

    switch (direction) {
      case 'up':
      case 'down':
        return { opacity: 1, y: 0 };
      case 'left':
      case 'right':
        return { opacity: 1, x: 0 };
      case 'none':
      default:
        return { opacity: 1 };
    }
  };

  return (
    <motion.div
      className={className}
      initial={getInitialPosition()}
      whileInView={getAnimatePosition()}
      viewport={{ once, amount }}
      transition={{
        duration: shouldReduceMotion ? 0 : duration,
        delay: shouldReduceMotion ? 0 : delay,
        ease: [0.25, 0.1, 0.25, 1], // Smooth easing
      }}
    >
      {children}
    </motion.div>
  );
};

export default ScrollReveal;
